import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { Tooltip, TooltipContent, TooltipProps, TooltipTrigger } from '../Tooltip';
import { cn } from '../../lib/styles';

const badgeVariants = cva('flex items-center justify-center w-fit transition-all flex-nowrap', {
  variants: {
    size: {
      xs: 'h-5 px-1.5 font-body-xs font-medium',
      sm: 'h-5 px-2 font-body-xs font-semibold',
      md: 'h-6.5 px-3.5 font-body-sm font-medium',
      lg: 'h-7 px-2.5 font-body-sm font-medium',
    },
    variant: {
      outline: 'px-1.5 border border-border-secondary text-text-primary',
      filled: 'px-2 text-text-primary',
      dot: 'px-2 gap-1 text-text-primary',
    },
    color: {
      default: 'hover:bg-bg-inter-secondary-default',
      lightGray: 'bg-gray-2 [&_.dot]:bg-gray-8',
      gray: 'bg-gray-4 [&_.dot]:bg-gray-12',
      red: 'bg-red-2 [&_.dot]:bg-red-8',
      green: 'bg-green-2 [&_.dot]:bg-green-8',
      teal: 'bg-teal-2 [&_.dot]:bg-teal-8',
      blue: 'bg-blue-2 [&_.dot]:bg-blue-8',
      boldBlue: 'bg-blue-3/60 text-blue-8 [&_.dot]:bg-blue-8',
      orange: 'bg-orange-3 [&_.dot]:bg-orange-8',
      yellow: 'bg-yellow-2 [&_.dot]:bg-yellow-8',
      violet: 'bg-violet-2 [&_.dot]:bg-violet-8',
    },
    shape: {
      pill: 'rounded-[100px]',
      circle: 'rounded-full aspect-square px-0',
    },
  },
  defaultVariants: {
    variant: 'outline',
    color: 'default',
    size: 'sm',
    shape: 'pill',
  },
});

export type BadgeColor = VariantProps<typeof badgeVariants>['color'];

export type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    tooltip?: React.ReactNode;
    tooltipProps?: TooltipProps;
    contentClassName?: string;
  };

function Badge({
  className,
  variant,
  color,
  asChild = false,
  tooltip,
  tooltipProps,
  size,
  shape,
  contentClassName,
  children,
  ...rest
}: BadgeProps) {
  const Comp: React.ElementType = asChild ? Slot : 'span';

  const badge = (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant, color, size, shape }), className)} {...rest}>
      {variant === 'dot' && <div className="dot w-1.5 h-1.5 rounded-full" />}

      <span className={cn('truncate', contentClassName)}>{children}</span>
    </Comp>
  );

  if (tooltip) {
    return (
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent withinPortal>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return badge;
}

export { Badge, badgeVariants };
